import { type FC, useCallback, type MouseEventHandler } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import serverVars from 'server-vars';
import { usePrerenderedCheckout } from 'dibs-checkout/exports/prerender/usePrerenderedCheckout.ts';
import { hidePage } from 'dibs-buyer-layout/exports/hidePage.ts';
import { trackPurchaseClick, type Location } from '../../tracking/actionButtonsTracking.ts';

import { Button } from 'dibs-elements/exports/Button.tsx';

import { type PdpSharedBuyItNowButton_item$key } from './__generated__/PdpSharedBuyItNowButton_item.graphql.ts';

const itemFragment = graphql`
    fragment PdpSharedBuyItNowButton_item on Item
    @argumentDefinitions(pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: pdp }) {
        ...usePrerenderedCheckout_item
        ...actionButtonsTracking_item
            @arguments(fetchAuction: false, pageDisplayEnum: $pageDisplayEnum)
    }
`;

const forcedCurrency = serverVars.get('forcedCurrency');

const variantMap = {
    marketplace: 'primary',
    auction: 'secondary',
} as const;

export const PdpSharedBuyItNowButton: FC<{
    item: PdpSharedBuyItNowButton_item$key;
    skuId?: string;
    quantitySelected?: string;
    variant?: 'marketplace' | 'auction';
    disabled?: boolean;
    size?: 'small' | 'medium' | 'large';
    location?: Location;
    className?: string;
}> = ({
    item: itemRef,
    skuId,
    quantitySelected,
    variant = 'marketplace',
    disabled,
    size = 'large',
    location,
    className,
}) => {
    const environment = useRelayEnvironment();
    const item = useFragment(itemFragment, itemRef);

    const { redirect: redirectToCheckout } = usePrerenderedCheckout({
        hideParentPage: hidePage,
        item,
        params: {
            skuId,
            quantity: quantitySelected,
            offerCurrency: forcedCurrency,
            triggerType: location === 'STICKY_HEADER' ? 'pdp-header-purchase' : undefined,
        },
    });

    const onClick: MouseEventHandler = useCallback(
        event => {
            trackPurchaseClick({
                item,
                event,
                location,
                environment,
                quantity: Number(quantitySelected),
            });
            redirectToCheckout();
        },
        [item, location, environment, quantitySelected, redirectToCheckout]
    );

    return (
        <Button
            fullWidth
            type={variantMap[variant]}
            size={size}
            onClick={onClick}
            disabled={disabled}
            className={className}
            dataTn="pdp-shared-buy-it-now-button"
        >
            <FormattedMessage id="PdpSharedBuyItNowButton.buyItNow" defaultMessage="Buy it now" />
        </Button>
    );
};
